import { useState } from "preact/hooks";

import type { TeamMemberBoard } from "../types";
import type { TeamPiece } from "../hooks/useTeamBoard";
import { colorForPerson, firstName } from "../lib/personColor";

export interface PersonRosterCardProps {
  member: TeamMemberBoard;
  tasks: TeamPiece[];
}

/**
 * Team roster card (design-spec-today-card.md §2's Person card): avatar +
 * name + "N of M done today" + expand chevron; expands to a READ-ONLY
 * per-person task list — never tappable here (a helper completes their
 * own work from their own Today, never from someone else looking at the
 * Team tab).
 *
 * The name line is the person's FULL display name, not `firstName()`. A
 * small crew often shares first names (or, in seeded/demo data, every
 * helper is "Worker One/Two/..."), and a roster of four identical
 * "Worker" cards gives no way to tell who is who. Full name was chosen
 * over "first name + last initial only on collision" because it reads the
 * same no matter who else is on the roster (a card's label never changes
 * when someone joins or leaves), needs no cross-card knowledge in this
 * component, and matches how People/Who's helping already label a person.
 * Long names wrap rather than truncate (`.person-summary-text`).
 */
export function PersonRosterCard({ member, tasks }: PersonRosterCardProps) {
  const [expanded, setExpanded] = useState(false);
  const totalToday = member.open_count + member.done_today_count;
  const detailId = `person-detail-${member.user_id}`;

  return (
    <div class={`person-card${expanded ? " is-expanded" : ""}`} data-person={member.user_id}>
      <button
        type="button"
        class="person-summary"
        aria-expanded={expanded}
        aria-controls={detailId}
        onClick={() => setExpanded((v) => !v)}
      >
        <span
          class="avatar"
          aria-hidden="true"
          style={{ background: colorForPerson(member.user_id) }}
        >
          {(firstName(member.name) || member.name).charAt(0)}
        </span>
        <span class="person-summary-text">
          <span class="person-name" style={{ display: "block" }}>
            {member.name}
          </span>
          <span class="person-progress" role="status" aria-live="polite">
            {member.done_today_count} of {totalToday} done today
          </span>
        </span>
        <span class="chevron" aria-hidden="true">
          ›
        </span>
      </button>
      {expanded ? (
        <ul id={detailId} class="person-detail">
          {tasks.length === 0 ? (
            <li class="empty-hint">Nothing on the list right now.</li>
          ) : (
            tasks.map(({ task, done }) => (
              <li
                key={task.id}
                class={`person-task-row${done ? " is-done" : ""}`}
                aria-label={`${task.title} — ${done ? "done" : "not done"}`}
              >
                <span class="dot" aria-hidden="true" />
                <span class="txt" aria-hidden="true">
                  {task.title}
                </span>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
